import { animate, style, transition, trigger } from '@angular/animations';
import {
  CdkDragDrop,
  moveItemInArray,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  OnInit,
  AfterViewInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { QuestionModel, QuizModel } from 'src/app/api/models';
import { PanelService } from './panel.service';
import { Panel, PanelEntity, PanelEntityType, SortAction } from './panel.types';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import {
  ImportContentDialogComponent,
  ImportContentDialogData,
} from '../common/import-content-dialog/import-content-dialog.component';
import { SubscriptionLimitService } from '../common/dialogs/subscription-limit-warning/subscription-limit.service';
import { LowerCasePipe, NgTemplateOutlet, NgClass } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { MatLabel } from '@angular/material/form-field';
import { MatList, MatListItem, MatListItemTitle } from '@angular/material/list';
import { MatRipple } from '@angular/material/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormToggleComponent } from '../common/form-field/toggle.component';
import { MatMenuButtonComponent } from '../common/mat-menu-button/mat-menu-button.component';
import { NgScrollbar } from 'ngx-scrollbar';
import { AddContentWizardComponent } from '../common/add-content-wizard/add-content-wizard.component';
import { AddContentDialogComponent } from '../common/dialogs/add-content-dialog/add-content-dialog.component';
import { LIST_ITEM_HEIGHT } from 'src/app/utility/app.utils';

@Component({
  selector: 'qa-panel',
  templateUrl: './panel.component.html',
  styleUrls: ['./panel.component.scss'],
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('100ms linear', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('100ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
  imports: [
    MatIconButton,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    ButtonComponent,
    MatLabel,
    NgScrollbar,
    NgTemplateOutlet,
    MatList,
    MatListItemTitle,
    CdkDropList,
    MatListItem,
    CdkDrag,
    MatRipple,
    CdkDragHandle,
    CdkDragPlaceholder,
    MatCheckbox,
    FormToggleComponent,
    MatMenuButtonComponent,
    TranslateModule,
    AddContentWizardComponent,
    LowerCasePipe,
    NgClass,
  ],
})
export class PanelComponent implements OnInit, AfterViewInit {
  @HostBinding('@fade') fade = true;
  @Input() panel: Panel;
  @Input() @HostBinding('class.is-open') isOpen = false;
  @ViewChild('addButton', { read: ElementRef }) addButton: ElementRef;
  @Output() opened = new EventEmitter<void>();
  @Output() itemSelected = new EventEmitter<PanelEntity>();
  toArray = Array.from;
  PanelEntityType = PanelEntityType;
  selectedItem: PanelEntity;
  LIST_ITEM_HEIGHT = LIST_ITEM_HEIGHT;

  @HostBinding('class.has-checkboxes')
  get hasSelectedItemActions() {
    return (
      this.panel.selectedItemActions && this.panel.selectedItemActions.length
    );
  }

  constructor(
    private panelService: PanelService,
    public activatedRoute: ActivatedRoute,
    private translator: TranslateService,
    private cdRef: ChangeDetectorRef,
    private subscriptionLimit: SubscriptionLimitService,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.panel.open$.subscribe(() => this.opened.emit());
    if (
      this.panel.sortingActions &&
      this.panel.sortingActions.length &&
      !this.panel.selectedSortAction
    ) {
      this.panel.selectedSortAction = _.cloneDeep(this.panel.sortingActions[0]);
    }
  }

  importContent() {
    if (this.panel.subscriptionLimitType) {
      const isLimitExceeded = this.subscriptionLimit.canAddEntity(
        this.panel.subscriptionLimitType
      );
      if (isLimitExceeded) {
        return;
      }
    }
    const dialog = this.matDialog.open<
      ImportContentDialogComponent,
      ImportContentDialogData
    >(ImportContentDialogComponent, {
      data: {
        imports: this.panel.imports,
        dataSource: null,
        title: this.getTitle(),
      },
      maxHeight: '90vh',
    });
    dialog.afterClosed().subscribe(() => {
      this.panelService.fetchItems(this.panel);
    });
  }

  openAddContentDialog() {
    if (this.panel.subscriptionLimitType) {
      const isLimitExceeded = this.subscriptionLimit.canAddEntity(
        this.panel.subscriptionLimitType
      );
      if (isLimitExceeded) {
        return;
      }
    }
    this.matDialog.open<AddContentDialogComponent>(AddContentDialogComponent, {
      data: {
        wizardImports: this.panel.wizardImports,
        dataSource: null,
        title: this.getTitle(),
      },
      autoFocus: false,
      maxHeight: '90vh',
      panelClass: 'add-content-dialog-panel-class',
    });
  }

  openGPT() {
    const gptImport = this.panel.imports.find((imp) => imp.id === 'chat-gpt');
    if (gptImport) {
      gptImport.action.click();
    }
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  afterItemDrop(
    event: CdkDragDrop<QuizModel | QuestionModel>,
    list: PanelEntity[]
  ) {
    moveItemInArray(list, event.previousIndex, event.currentIndex);
    const parentId = this.panelService.getParentItemId(this.panel.type);
    this.panelService
      .getPanel(this.panel.type)
      .changeItemPosition(event.item.data.id, parentId, event.currentIndex + 1)
      .pipe(switchMap(() => this.panelService.fetchItems(this.panel)))
      .subscribe({
        next: () => {
          this.panelService.courseUpdated$.next(true);
        },
      });
  }

  getItemSelectedText(singular: boolean) {
    switch (this.panel.type) {
      case PanelEntityType.FlashCard:
        return (
          this.translator.instant('common.label.indexCard') +
          `${singular ? '' : this.translator.instant('common.label.n')} ` +
          this.translator.instant('common.label.selected')
        );
      case PanelEntityType.FlashCardStack:
        return this.translator.instant('common.label.stackSelected');
      case PanelEntityType.Question:
        return (
          this.translator.instant('common.label.question') +
          `${singular ? '' : this.translator.instant('common.label.n')} ` +
          this.translator.instant('common.label.selected')
        );
      case PanelEntityType.Quiz:
        return this.translator.instant('common.label.quizSelected');
      case PanelEntityType.ExamQuestion:
        return (
          this.translator.instant('common.label.question') +
          `${singular ? '' : this.translator.instant('common.label.n')} ` +
          this.translator.instant('common.label.selected')
        );
      case PanelEntityType.Exam:
        return this.translator.instant('common.label.examSelected');
      case PanelEntityType.Event:
        return (
          `${
            singular
              ? this.translator.instant('common.label.liveEvent')
              : this.translator.instant('common.label.liveEvents')
          } ` + this.translator.instant('common.label.selected')
        );
      default:
        return '';
    }
  }

  getTitle() {
    switch (this.panel.type) {
      case PanelEntityType.FlashCard:
        return this.translator.instant(
          'content.wizardActions.title.createFlashCard'
        );
      case PanelEntityType.FlashCardStack:
        return this.translator.instant(
          'content.wizardActions.title.createStack'
        );
      case PanelEntityType.Question:
      case PanelEntityType.ExamQuestion:
      case PanelEntityType.EventQuestion:
        return this.translator.instant(
          'content.wizardActions.title.createQuestion'
        );
      case PanelEntityType.Quiz:
        return this.translator.instant(
          'content.wizardActions.title.createQuiz'
        );
      case PanelEntityType.Exam:
        return this.translator.instant(
          'content.wizardActions.title.createExam'
        );
      case PanelEntityType.Event:
        return this.translator.instant(
          'content.wizardActions.title.createLiveEvent'
        );
      default:
        return '';
    }
  }

  isCurrentItem(panel: Panel, item: PanelEntity): boolean {
    return (
      panel.childTypes &&
      panel.childTypes.length &&
      +this.activatedRoute.snapshot.paramMap.get(panel.type) === item.id
    );
  }

  pathToThisPanel() {
    return this.activatedRoute.snapshot.params;
  }

  onItemClick(item: PanelEntity) {
    const types = [PanelEntityType.Question, PanelEntityType.FlashCard];
    if (!types.includes(this.panel.type)) {
      this.panelService.addParam(this.panel.type, item.id);
    } else {
      this.selectedItem = item;
      this.itemSelected.emit(item);
    }
  }

  deselectEntity(event) {
    event.stopPropagation();
    this.selectedItem = null;
    this.itemSelected.emit(null);
  }

  sortList() {
    this.panelService.sortPanelListItems(this.panel);
  }

  changeSort(item: SortAction) {
    if (this.panel.selectedSortAction.text !== item.text) {
      this.panel.selectedSortAction = _.cloneDeep(item);
      this.panelService.sortPanelItems(this.panel);
      this.panelService.setSortOption(this.panel, item);
    }
  }

  back() {
    this.panelService.removeParam(this.panel.parentType);
  }

  toggleActive(item) {
    this.panel.activateItemAction.onClick(item);
  }

  selectAll(checked: boolean) {
    if (checked) {
      this.panel.list.forEach((item) =>
        this.panel.selectedItemIds.add(item.id)
      );
    } else {
      this.panel.selectedItemIds.clear();
    }
  }

  get someChecked(): boolean {
    return (
      this.panel.selectedItemIds &&
      this.panel?.list.length > this.panel.selectedItemIds.size &&
      this.panel.selectedItemIds.size > 0
    );
  }

  get isAllChecked(): boolean {
    return (
      this.panel.selectedItemIds &&
      this.panel?.list.length === this.panel.selectedItemIds.size &&
      this.panel.selectedItemIds.size > 0
    );
  }
}
